import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, Plus, MoreVertical, Trash2, Pencil, Users,
  ArrowRight, Palette, BookmarkPlus, LayoutTemplate, Eraser, X,
  Handshake, Kanban as KanbanIcon, Upload, Sparkles, Link2,
  PanelLeftClose, PanelLeftOpen, Search, GripVertical, Check,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";
import ImportLeadsDialog from "@/components/ImportLeadsDialog";
import PublicCaptureLinks from "@/components/PublicCaptureLinks";
import SmartListsBar from "@/components/SmartListsBar";
import { leadNbaHint, HINT_TONE_STYLE } from "@/lib/nbaHint";
import { useAuth } from "@/context/AuthContext";
import Dispo from "@/pages/Dispo";


const COLOR_STYLES = {
  neutral: { head: "bg-neutral-100 border-neutral-300", dot: "bg-neutral-400", text: "text-neutral-700" },
  blue:    { head: "bg-blue-50    border-blue-300",    dot: "bg-blue-500",    text: "text-blue-700" },
  sky:     { head: "bg-sky-50     border-sky-300",     dot: "bg-sky-500",     text: "text-sky-700" },
  amber:   { head: "bg-amber-50   border-amber-300",   dot: "bg-amber-500",   text: "text-amber-700" },
  orange:  { head: "bg-orange-50  border-orange-300",  dot: "bg-orange-500",  text: "text-orange-700" },
  green:   { head: "bg-green-50   border-green-300",   dot: "bg-green-500",   text: "text-green-700" },
  emerald: { head: "bg-emerald-50 border-emerald-300", dot: "bg-emerald-500", text: "text-emerald-700" },
  red:     { head: "bg-red-50     border-red-300",     dot: "bg-red-500",     text: "text-red-700" },
  rose:    { head: "bg-rose-50    border-rose-300",    dot: "bg-rose-500",    text: "text-rose-700" },
  violet:  { head: "bg-violet-50  border-violet-300",  dot: "bg-violet-500",  text: "text-violet-700" },
  indigo:  { head: "bg-indigo-50  border-indigo-300",  dot: "bg-indigo-500",  text: "text-indigo-700" },
};
const COLOR_KEYS = Object.keys(COLOR_STYLES);

function money(n) {
  if (n == null) return null;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function Boards() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "dispo" ? "dispo" : "board";
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "board") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const { can } = useAuth();
  const canClear = can ? can("lead.delete") : false;
  const [boards, setBoards] = useState([]);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buckets, setBuckets] = useState({});
  const [columns, setColumns] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [clearColState, setClearColState] = useState(null); // {key,label,count}
  const [renameValue, setRenameValue] = useState("");
  const [editColsOpen, setEditColsOpen] = useState(false);
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const navigate = useNavigate();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [captureLinksOpen, setCaptureLinksOpen] = useState(false);
  const [dupePreview, setDupePreview] = useState(null); // {duplicate_leads, sample_clusters, ...}
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [enrichPreview, setEnrichPreview] = useState(null); // {total_missing, sample}
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichRunning, setEnrichRunning] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [enrichLimit, setEnrichLimit] = useState(100);
  const [findOwnerOpen, setFindOwnerOpen] = useState(false);

  // Collapsible left rail — persist across reloads
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const v = localStorage.getItem("boards.sidebarOpen");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("boards.sidebarOpen", sidebarOpen ? "1" : "0"); } catch (_) { /* ignore */ }
  }, [sidebarOpen]);

  // Filter chips — narrow the board to your most promising leads.
  const [filters, setFilters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("boards.filters") || "{}") || {};
    } catch { return {}; }
  });
  // Named call-list buckets (populated from /api/leads/lists) — lets the rep
  // narrow the whole board to just "Cape Coral Absentee 6k" and dial from
  // there without every list mixing into the New column.
  const [availableLists, setAvailableLists] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/leads/lists")
      .then(({ data }) => { if (!cancelled) setAvailableLists(data.lists || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Pre-select a list filter if the URL says ?list=<name> (used by the
  // "View on board" button on the Lead Lists page).
  useEffect(() => {
    const requested = searchParams.get("list");
    if (!requested) return;
    setFilters((f) => {
      const next = { ...f, list_name: requested };
      try { localStorage.setItem("boards.filters", JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
    // Consume the URL param so future refreshes don't re-apply it
    const p = new URLSearchParams(searchParams);
    p.delete("list");
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleFilter = (k) => setFilters((f) => {
    const next = { ...f, [k]: !f[k] };
    try { localStorage.setItem("boards.filters", JSON.stringify(next)); } catch (_) { /* ignore */ }
    return next;
  });
  const setListFilter = (name) => setFilters((f) => {
    const next = { ...f, list_name: name || null };
    if (!name) delete next.list_name;
    try { localStorage.setItem("boards.filters", JSON.stringify(next)); } catch (_) { /* ignore */ }
    return next;
  });
  const clearFilters = () => {
    setFilters({});
    try { localStorage.removeItem("boards.filters"); } catch (_) { /* ignore */ }
  };
  const activeFilterCount =
    Object.entries(filters).filter(([k, v]) => k === "list_name" ? !!v : v === true).length;

  const matchesFilters = (lead) => {
    if (filters.equity_100k && !(Number(lead.property_equity) >= 100_000)) return false;
    if (filters.equity_50pct && !(Number(lead.property_equity_pct) >= 50)) return false;
    if (filters.distress_50 && !(Number(lead.distress_score ?? lead.motivation_score) >= 50)) return false;
    if (filters.absentee_only && lead.owner_occupied !== false) return false;
    if (filters.has_phone && !(lead.phone && String(lead.phone).replace(/\D/g, "").length >= 10)) return false;
    if (filters.not_listed && (lead.listed_for_sale === true || lead.listed_fsbo === true)) return false;
    if (filters.list_name && (lead.list_name || "") !== filters.list_name) return false;
    return true;
  };

  // Auto-detect duplicate leads on load — user shouldn't have to hunt for the button
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/leads-dedupe-preview");
        if (!cancelled) setDupePreview(data);
      } catch (_) { /* silent — banner just won't show */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-detect leads missing phones — surface the bulk-enrich button as a nudge
  const loadEnrichPreview = async () => {
    try {
      const { data } = await api.get("/leads/bulk-skip-trace-missing/preview");
      setEnrichPreview(data);
      return data;
    } catch (_) { return null; }
  };
  useEffect(() => { loadEnrichPreview(); }, []);

  // Find the "No Contact New Lead" column on the active board (case/spacing tolerant).
  // Returns { board_id, column_key } if found, else null.
  const findNoContactColumn = () => {
    const board = boards.find((b) => b.id === activeBoardId) || boards[0];
    if (!board) return null;
    const cols = board.columns || [];
    // Try exact/substring match — accept variations like "No Contact", "No-Contact New Lead", etc.
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const target = cols.find((c) => norm(c.label).includes("no contact"))
                || cols.find((c) => norm(c.label).includes("new lead") && !norm(c.label).includes("unq"));
    if (!target) return null;
    return { board_id: board.id, column_key: target.key, column_label: target.label };
  };

  const runBulkEnrich = async (overrideLimit) => {
    setEnrichRunning(true);
    setEnrichResult(null);
    const dest = findNoContactColumn();
    try {
      const { data } = await api.post("/leads/bulk-skip-trace-missing", {
        limit: Number(overrideLimit ?? enrichLimit) || 100,
        board_id:           dest?.board_id || null,
        move_to_column_key: dest?.column_key || null,
      });
      setEnrichResult(data);
      const movedStr = data.moved > 0
        ? ` · moved ${data.moved} → "${dest?.column_label || "No Contact"}"`
        : "";
      toast.success(
        `Found phones on ${data.found}/${data.processed} leads` +
        (data.reapi_credits_used ? ` · ${data.reapi_credits_used} REAPI credits` : "") +
        movedStr
      );
      // Refresh preview + board so freshly-enriched leads show their phones
      await loadEnrichPreview();
      if (activeBoardId) loadBoardData(activeBoardId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk enrich failed");
    } finally { setEnrichRunning(false); }
  };

  // 1-click "Skip-trace next 100" — no dialog, just grinds through the queue
  const runQuickEnrich100 = async () => {
    if (enrichRunning) return;
    await runBulkEnrich(100);
  };

  const runDedupe = async () => {
    setDedupeRunning(true);
    try {
      const { data } = await api.post("/leads/dedupe-scan");
      const l = data.leads || {};
      const p = data.properties || {};
      toast.success(
        `Merged ${l.leads_merged || 0} duplicate leads · ` +
        `deleted ${l.leads_deleted || 0} · ` +
        `cleaned ${p.properties_deleted || 0} duplicate properties`
      );
      setDupePreview({ duplicate_leads: 0, sample_clusters: [] });
      if (activeBoardId) loadBoardData(activeBoardId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dedupe failed");
    } finally { setDedupeRunning(false); }
  };

  // ---------- Drag & drop (HTML5, no library) ----------
  const dragRef = useRef({ leadId: null, from: null });
  // Separate ref for the sidebar rail's board-row drag (independent of
  // the lead-card drag above). Reorders the pipeline rail top-to-bottom
  // and persists the new `order` back to /boards/:id (2026-02-17 owner
  // ask — CRM pipelines should be as re-orderable as the sidebar nav).
  const railDragId = useRef(null);
  // Edit-mode toggle for the board — reps have to explicitly ARM edit
  // mode via the ✎ button next to the board title before they can
  // remove columns or rename anything. Prevents "I accidentally
  // clicked X" from wiping a stage mid-pipeline. Not persisted — every
  // page load starts in view mode.
  const [boardEditMode, setBoardEditMode] = useState(false);
  const reorderBoardRail = async (droppedId, overId) => {
    if (!droppedId || droppedId === overId) return;
    const ids = boards.map((b) => b.id).filter((id) => id !== droppedId);
    const idx = ids.indexOf(overId);
    ids.splice(idx < 0 ? ids.length : idx, 0, droppedId);
    // Optimistic UI — reorder locally first, then persist. On error
    // we snapshot-restore so the rail doesn't lie about DB state.
    const snapshot = boards;
    const next = ids.map((id, i) => ({
      ...boards.find((b) => b.id === id),
      order: i,
    }));
    setBoards(next);
    try {
      // Persist every row's new order in parallel so one slow request
      // doesn't block the others.
      await Promise.all(
        next.map((b) => api.patch(`/boards/${b.id}`, { order: b.order })),
      );
      toast.success("Pipeline order saved");
    } catch (e) {
      setBoards(snapshot);
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  const loadBoards = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/boards");
      setBoards(data.boards || []);
      // Honor ?board_id=… so /boards?board_id=xxx&lead_id=yyy lands on
      // the right board. Falls back to the first board otherwise.
      // Owner ask 2026-08-27: Send to Acquisitions needed to actually
      // put the rep on the Acquisitions board.
      const urlBoardId = searchParams.get("board_id");
      if (urlBoardId && (data.boards || []).some((b) => b.id === urlBoardId)) {
        setActiveBoardId(urlBoardId);
      } else if (!activeBoardId && (data.boards || []).length > 0) {
        setActiveBoardId(data.boards[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load boards");
    } finally { setLoading(false); }
  };

  // Auto-open the lead detail drawer when arriving via ?lead_id=…
  useEffect(() => {
    const urlLeadId = searchParams.get("lead_id");
    if (urlLeadId) setDrawerLeadId(urlLeadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBoardData = async (bid) => {
    if (!bid) return;
    setRefreshing(true);
    try {
      const { data } = await api.get(`/boards/${bid}/leads`);
      setBuckets(data.buckets || {});
      setColumns(data.columns || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load board leads");
    } finally { setRefreshing(false); }
  };

  useEffect(() => { loadBoards(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadBoardData(activeBoardId); }, [activeBoardId]);

  // Live board sync — silently re-fetch buckets every 20s while the tab is
  // visible and no drag is in progress. Keeps every teammate on the same
  // page without a manual refresh.
  useEffect(() => {
    if (!activeBoardId) return;
    const iv = setInterval(async () => {
      if (document.hidden) return;
      if (dragRef.current?.leadId) return; // pause during an active drag
      try {
        const { data } = await api.get(`/boards/${activeBoardId}/leads`);
        setBuckets(data.buckets || {});
        setColumns(data.columns || []);
      } catch { /* offline — ignore this tick */ }
    }, 20000);
    return () => clearInterval(iv);
  }, [activeBoardId]);

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId),
    [boards, activeBoardId],
  );

  const leadTotal = useMemo(
    () => Object.values(buckets).reduce((n, arr) => n + (arr?.length || 0), 0),
    [buckets],
  );

  // ---------- Drag & drop (HTML5, no library) ----------
  const onDragStart = (leadId, fromKey) => (e) => {
    dragRef.current = { leadId, from: fromKey };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", leadId);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (toKey) => async (e) => {
    e.preventDefault();
    const { leadId, from } = dragRef.current;
    dragRef.current = { leadId: null, from: null };
    if (!leadId || from === toKey) return;
    // Optimistic move
    setBuckets((prev) => {
      const next = { ...prev };
      const src = (next[from] || []).slice();
      const idx = src.findIndex((l) => l.id === leadId);
      if (idx === -1) return prev;
      const [item] = src.splice(idx, 1);
      next[from] = src;
      next[toKey] = [{ ...item, column_key: toKey }, ...(next[toKey] || [])];
      return next;
    });
    try {
      await api.post(`/boards/${activeBoardId}/move-lead`, {
        lead_id: leadId, column_key: toKey,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
      loadBoardData(activeBoardId);
    }
  };

  const attachUnassigned = async () => {
    if (!activeBoardId) return;
    setAttaching(true);
    try {
      const { data } = await api.post(
        `/boards/${activeBoardId}/assign-unassigned`, {},
      );
      toast.success(
        data.attached > 0
          ? `Added ${data.attached} existing lead${data.attached === 1 ? "" : "s"} to "${activeBoard?.name}"`
          : "No unassigned leads found",
      );
      loadBoardData(activeBoardId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setAttaching(false); }
  };

  const createBoard = async (name) => {
    try {
      const { data } = await api.post("/boards", {
        name,
        columns: [
          { label: "To Do",       color: "sky",     order: 0 },
          { label: "In Progress", color: "amber",   order: 1 },
          { label: "Done",        color: "emerald", order: 2 },
        ],
      });
      setBoards((prev) => [...prev, data]);
      setActiveBoardId(data.id);
      toast.success(`Board "${name}" created`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const renameBoard = async () => {
    if (!renameValue.trim() || !activeBoard) return;
    try {
      const { data } = await api.patch(`/boards/${activeBoard.id}`,
                                       { name: renameValue.trim() });
      setBoards((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      toast.success("Board renamed");
      setRenameOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const deleteBoard = async () => {
    if (!activeBoard) return;
    if (!confirm(`Delete "${activeBoard.name}"? Leads on this board will be detached (not deleted).`))
      return;
    try {
      await api.delete(`/boards/${activeBoard.id}`);
      const remaining = boards.filter((b) => b.id !== activeBoard.id);
      setBoards(remaining);
      setActiveBoardId(remaining[0]?.id || null);
      toast.success("Board deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // One-click nuke — wipes every lead on the board. Single browser confirm.
  const clearBoard = async () => {
    if (!activeBoard) return;
    const total = Object.values(buckets).reduce((s, arr) => s + arr.length, 0);
    if (total === 0) {
      toast.info("This board is already empty");
      return;
    }
    if (!confirm(`Delete ALL ${total} lead${total === 1 ? "" : "s"} on "${activeBoard.name}"? This cannot be undone.`))
      return;
    try {
      const { data } = await api.post(`/boards/${activeBoard.id}/clear-all`);
      toast.success(`Cleared ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      loadBoardData(activeBoard.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  // Per-column clear (called from ClearColumnDialog after user types the column name)
  const confirmClearColumn = async () => {
    if (!activeBoard || !clearColState) return;
    try {
      const { data } = await api.post(
        `/boards/${activeBoard.id}/columns/${clearColState.key}/clear`,
      );
      toast.success(`Cleared ${data.deleted} lead${data.deleted === 1 ? "" : "s"} from "${clearColState.label}"`);
      setClearColState(null);
      loadBoardData(activeBoard.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const saveColumns = async (cols) => {
    try {
      const { data } = await api.patch(`/boards/${activeBoard.id}`, {
        columns: cols,
      });
      setBoards((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      setColumns(data.columns);
      toast.success("Columns updated");
      setEditColsOpen(false);
      loadBoardData(activeBoard.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveAsTemplate = async (name) => {
    if (!activeBoard) return;
    try {
      await api.post(`/board-templates/from-board/${activeBoard.id}`, { name });
      toast.success(`Template "${name}" saved`);
      setSaveTplOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const createFromTemplate = async (templateId, boardName) => {
    try {
      const { data } = await api.post("/boards/from-template", {
        template_id: templateId, board_name: boardName,
      });
      setBoards((prev) => [...prev, data]);
      setActiveBoardId(data.id);
      setTemplatesOpen(false);
      toast.success(`Board "${data.name}" created from template`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] md:h-screen" data-testid="boards-page">
      {/* SMART LISTS BAR (Phase 2) — saved filter chips + rule builder */}
      <div className="px-4 py-2 border-b border-[var(--app-line)] bg-white shrink-0">
        <SmartListsBar
          onApply={({ id, name, count }) => {
            // eslint-disable-next-line no-console
            console.log(`[SmartList] applied ${name} (${count} leads)`);
          }}
        />
      </div>
      {/* TAB BAR — Board / Dispo */}
      <div className="px-4 py-2 border-b border-[var(--app-line)] bg-white flex items-center gap-1 shrink-0"
           data-testid="crm-tabs">        <button
          onClick={() => setActiveTab("board")}
          data-testid="tab-crm-board"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors flex items-center gap-1.5 ${
            activeTab === "board"
              ? "bg-[var(--app-black)] text-white"
              : "text-muted-foreground hover:bg-[var(--app-surface)]"
          }`}
        >
          <KanbanIcon size={12} /> Board
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setImportOpen(true)}
          data-testid="crm-import-csv-btn"
          className="px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors flex items-center gap-1.5 border border-[var(--app-line)] hover:bg-[var(--app-surface)] text-[var(--app-black)]"
          title="Bulk upload leads from a CSV / Excel file"
        >
          <Upload size={12} /> Import CSV
        </button>
        {enrichPreview && enrichPreview.total_missing > 0 && (
          <div className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 overflow-hidden"
               data-testid="crm-bulk-enrich-group">
            <button
              onClick={runQuickEnrich100}
              disabled={enrichRunning}
              data-testid="crm-quick-enrich-100-btn"
              className="px-3 py-1.5 text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5 hover:bg-emerald-100 disabled:opacity-60"
              title="Skip-trace the next 100 leads missing phones · runs immediately"
            >
              {enrichRunning
                ? <Loader2 size={12} className="animate-spin" />
                : <Sparkles size={12} />}
              Skip-trace next 100
              <span className="text-[10px] opacity-70 normal-case">
                / {enrichPreview.total_missing.toLocaleString()}
              </span>
            </button>
            <button
              onClick={() => setEnrichOpen(true)}
              data-testid="crm-bulk-enrich-btn"
              className="px-2 py-1.5 border-l border-emerald-300 hover:bg-emerald-100"
              title="Advanced: pick batch size, preview sample"
            >
              <MoreVertical size={12} />
            </button>
          </div>
        )}
        <button
          onClick={async () => {
            if (!window.confirm(
              "Move every lead that already has a phone from \"New Lead (Unqualified)\" " +
              "to \"No Contact New Lead\"?\n\n" +
              "This is a one-shot cleanup — no REAPI credits used. Safe to run any time."
            )) return;
            try {
              const { data } = await api.post("/leads/reflow-no-contact");
              if (data.moved > 0) {
                toast.success(`Moved ${data.moved} lead${data.moved === 1 ? "" : "s"} to No Contact New Lead`);
                if (activeBoardId) loadBoardData(activeBoardId);
              } else {
                toast.info(`Scanned ${data.scanned} — nothing to move`);
              }
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Reflow failed");
            }
          }}
          data-testid="crm-reflow-no-contact-btn"
          className="px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors flex items-center gap-1.5 border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800"
          title="One-shot cleanup — move any lead already skip-traced to No Contact New Lead (no credits)"
        >
          <ArrowRight size={12} /> Reflow to No Contact
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(
              "Scan all your leads and merge duplicates by address? " +
              "This will keep the oldest copy of each address and merge " +
              "phone/email/notes from any duplicates. Safe to run any time."
            )) return;
            await runDedupe();
          }}
          data-testid="crm-dedupe-btn"
          className="px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors flex items-center gap-1.5 border border-[var(--app-line)] hover:bg-[var(--app-surface)] text-[var(--app-black)]"
          title="Merge duplicate leads sharing the same address"
        >
          <Eraser size={12} /> Cleanup Dupes
        </button>
      </div>

      {/* Duplicate-lead auto-detect banner — pops up when the account has
          existing dupes so the user doesn't have to hunt for the Cleanup
          Dupes button. Hidden once dupes hit zero. */}
      {dupePreview && dupePreview.duplicate_leads > 0 && activeTab === "crm" && (
        <div className="mx-4 mt-2 mb-1 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 text-sm"
             data-testid="dedupe-banner">
          <div className="w-8 h-8 rounded-full bg-amber-400/20 text-amber-800 flex items-center justify-center shrink-0">
            <Eraser size={14} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-amber-900">
              {dupePreview.duplicate_leads} duplicate lead{dupePreview.duplicate_leads === 1 ? "" : "s"} detected
            </div>
            <div className="text-[11px] text-amber-800/80">
              Across {dupePreview.duplicate_clusters} address
              {dupePreview.duplicate_clusters === 1 ? "" : "es"}
              {dupePreview.sample_clusters?.[0]?.address && (
                <> · e.g. <i>{dupePreview.sample_clusters[0].address}</i> ({dupePreview.sample_clusters[0].count}×)</>
              )}
              . Merging keeps the oldest copy + phone/email/notes from each duplicate.
            </div>
          </div>
          <button
            onClick={runDedupe}
            disabled={dedupeRunning}
            data-testid="dedupe-banner-btn"
            className="px-3 py-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-50 shrink-0"
          >
            {dedupeRunning ? "Merging…" : "Merge duplicates"}
          </button>
        </div>
      )}

      {activeTab === "dispo" ? (
        <div className="flex-1 overflow-y-auto"><Dispo /></div>
      ) : (
      <div className="flex flex-1 min-h-0">
      {/* LEFT RAIL: board picker (collapsible) */}
      {sidebarOpen ? (
      <aside className="w-56 shrink-0 border-r border-[var(--app-line)] bg-white overflow-y-auto"
             data-testid="boards-rail">
        <div className="p-3 flex items-center justify-between border-b border-[var(--app-line)]">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Pipelines
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setNewBoardOpen(true)}
              className="p-1 rounded hover:bg-[var(--app-surface)]"
              title="New board"
              data-testid="boards-new-btn"
            >
              <Plus size={13} />
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded hover:bg-[var(--app-surface)] text-muted-foreground"
              title="Hide pipelines"
              data-testid="boards-rail-collapse"
            >
              <PanelLeftClose size={13} />
            </button>
          </div>
        </div>
        {loading ? (
          <div className="p-4 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="py-1">
            {boards.map((b) => (
              <div
                key={b.id}
                draggable
                onDragStart={(e) => {
                  railDragId.current = b.id;
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", b.id); } catch (_) { /* Safari */ }
                }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const dropped = railDragId.current;
                  railDragId.current = null;
                  reorderBoardRail(dropped, b.id);
                }}
                onClick={() => setActiveBoardId(b.id)}
                data-testid={`boards-rail-${b.id}`}
                className={`group w-full px-3 py-2 text-sm flex items-center gap-2 border-l-2 transition-colors cursor-pointer ${
                  b.id === activeBoardId
                    ? "border-[var(--app-black)] bg-[var(--app-surface)] font-medium"
                    : "border-transparent hover:bg-[var(--app-surface)]"
                }`}
              >
                <GripVertical
                  size={11}
                  className={`text-slate-400 shrink-0 cursor-grab ${
                    b.id === activeBoardId ? "opacity-60" : "opacity-0 group-hover:opacity-60"
                  }`}
                  data-testid={`boards-rail-grip-${b.id}`}
                />
                <span className="flex-1 truncate">{b.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {(b.columns || []).length}
                </span>
                {/* Per-row kebab: rename + delete. Hidden until hover
                    so the rail stays clean, always visible on the
                    active board. Owner ask 2026-02-17 — pipelines
                    rail should be as editable as CRM column headers. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveBoardId(b.id); }}
                      className={`p-1 rounded hover:bg-white shrink-0 ${
                        b.id === activeBoardId
                          ? "opacity-70"
                          : "opacity-0 group-hover:opacity-70"
                      }`}
                      title="Rename or delete this pipeline"
                      data-testid={`boards-rail-menu-${b.id}`}
                    >
                      <MoreVertical size={12} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44"
                                       onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveBoardId(b.id);
                        setRenameValue(b.name);
                        setRenameOpen(true);
                      }}
                      data-testid={`boards-rail-rename-${b.id}`}
                    >
                      <Pencil size={12} className="mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveBoardId(b.id);
                        setEditColsOpen(true);
                      }}
                      data-testid={`boards-rail-cols-${b.id}`}
                    >
                      <Palette size={12} className="mr-2" /> Edit columns
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!confirm(`Delete "${b.name}"? Leads on this board will be detached (not deleted).`))
                          return;
                        try {
                          await api.delete(`/boards/${b.id}`);
                          const remaining = boards.filter((x) => x.id !== b.id);
                          setBoards(remaining);
                          if (b.id === activeBoardId) {
                            setActiveBoardId(remaining[0]?.id || null);
                          }
                          toast.success("Board deleted");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Delete failed");
                        }
                      }}
                      className="text-red-600 focus:text-red-700"
                      data-testid={`boards-rail-delete-${b.id}`}
                    >
                      <Trash2 size={12} className="mr-2" /> Delete pipeline
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </aside>
      ) : (
        <div className="w-8 shrink-0 border-r border-[var(--app-line)] bg-white flex flex-col items-center py-2"
             data-testid="boards-rail-collapsed">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded hover:bg-[var(--app-surface)] text-muted-foreground"
            title="Show pipelines"
            data-testid="boards-rail-expand"
          >
            <PanelLeftOpen size={14} />
          </button>
          <div className="mt-3 text-[9px] uppercase tracking-widest text-muted-foreground rotate-180 [writing-mode:vertical-rl]">
            Pipelines
          </div>
        </div>
      )}

      {/* MAIN: board */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-6 py-3 border-b border-[var(--app-line)] bg-white">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight truncate"
                  data-testid="boards-title">
                {activeBoard?.name || "—"}
              </h1>
              {activeBoard && (
                <button
                  onClick={() => setBoardEditMode((v) => !v)}
                  className={`p-1.5 rounded-full border transition-colors ${
                    boardEditMode
                      ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                      : "border-transparent hover:bg-[var(--app-surface)] text-slate-400 hover:text-slate-700"
                  }`}
                  title={boardEditMode ? "Done editing (locks column-remove)" : "Edit board (unlock column-remove)"}
                  data-testid="boards-edit-toggle"
                >
                  {boardEditMode ? <Check size={13} /> : <Pencil size={13} />}
                </button>
              )}
              {boardEditMode && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200">
                  Edit mode
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
              <span>
                {columns.length} column{columns.length === 1 ? "" : "s"} · {leadTotal} lead{leadTotal === 1 ? "" : "s"}
              </span>
              <span className="flex items-center gap-1" data-testid="live-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] uppercase tracking-widest text-emerald-700">Live</span>
              </span>
              {refreshing && <span className="text-[10px]">· syncing…</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFindOwnerOpen(true)}
              data-testid="boards-find-owner-btn"
              className="h-8 rounded-full text-xs gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
              title="Search any owner already in the system and place them into a board column"
            >
              <Search size={12} /> Find owner
            </Button>
            {activeBoard && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 rounded-full hover:bg-[var(--app-surface)]"
                          data-testid="boards-menu-btn">
                    <MoreVertical size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    onClick={attachUnassigned}
                    disabled={attaching || !activeBoardId}
                    data-testid="boards-attach-unassigned"
                  >
                    {attaching
                      ? <Loader2 className="animate-spin mr-2" size={12} />
                      : <Users size={12} className="mr-2" />}
                    Attach existing leads
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setRenameValue(activeBoard.name); setRenameOpen(true); }}
                                    disabled={!boardEditMode}
                                    data-testid="boards-rename">
                    <Pencil size={12} className="mr-2" /> Rename
                    {!boardEditMode && <span className="ml-auto text-[9px] opacity-60">🔒</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setEditColsOpen(true)}
                                    disabled={!boardEditMode}
                                    data-testid="boards-edit-cols">
                    <Palette size={12} className="mr-2" /> Edit columns
                    {!boardEditMode && <span className="ml-auto text-[9px] opacity-60">🔒</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSaveTplOpen(true)}
                                    data-testid="boards-save-tpl">
                    <BookmarkPlus size={12} className="mr-2" /> Save as template
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTemplatesOpen(true)}
                                    data-testid="boards-open-tpls">
                    <LayoutTemplate size={12} className="mr-2" /> My templates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setCaptureLinksOpen(true)}
                                    data-testid="boards-capture-links">
                    <Link2 size={12} className="mr-2" /> Public capture link
                  </DropdownMenuItem>
                  {canClear && (
                    <DropdownMenuItem onClick={clearBoard}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid="boards-clear-all">
                      <Eraser size={12} className="mr-2" /> Clear all leads
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={deleteBoard}
                                    disabled={!boardEditMode}
                                    className="text-red-600 focus:text-red-700"
                                    data-testid="boards-delete">
                    <Trash2 size={12} className="mr-2" /> Delete board
                    {!boardEditMode && <span className="ml-auto text-[9px] opacity-60">🔒</span>}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-x-auto overflow-y-hidden bg-[var(--app-surface)]"
             data-testid="boards-canvas">
          {/* Filter chips — zero in on your best leads */}
          <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[var(--app-line)] bg-white/60 backdrop-blur-sm overflow-x-auto"
               data-testid="boards-filter-bar">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 mr-1">
              Filter
            </span>
            {[
              { key: "equity_100k",  label: "Equity > $100k",     tone: "emerald" },
              { key: "equity_50pct", label: "Equity ≥ 50%",       tone: "emerald" },
              { key: "distress_50",  label: "Distress > 50",      tone: "rose" },
              { key: "absentee_only",label: "Absentee only",      tone: "violet" },
              { key: "has_phone",    label: "Has phone",          tone: "sky" },
              { key: "not_listed",   label: "Not on MLS/FSBO",    tone: "amber" },
            ].map((f) => {
              const active = !!filters[f.key];
              const toneCls = active
                ? {
                    emerald: "bg-emerald-500 text-white border-emerald-500",
                    rose:    "bg-rose-500 text-white border-rose-500",
                    violet:  "bg-violet-500 text-white border-violet-500",
                    sky:     "bg-sky-500 text-white border-sky-500",
                    amber:   "bg-amber-500 text-white border-amber-500",
                  }[f.tone]
                : "bg-white text-muted-foreground border-[var(--app-line)] hover:bg-[var(--app-surface)]";
              return (
                <button
                  key={f.key}
                  onClick={() => toggleFilter(f.key)}
                  data-testid={`boards-filter-${f.key}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition shrink-0 ${toneCls}`}
                >
                  {f.label}
                </button>
              );
            })}
            {availableLists.length > 0 && (
              <div className="flex items-center gap-1 shrink-0"
                   data-testid="boards-filter-list-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">List</span>
                <select
                  value={filters.list_name || ""}
                  onChange={(e) => setListFilter(e.target.value)}
                  data-testid="boards-filter-list-select"
                  className={`text-[11px] px-2 py-1 rounded-full border transition ${
                    filters.list_name
                      ? "bg-indigo-500 text-white border-indigo-500 font-medium"
                      : "bg-white text-muted-foreground border-[var(--app-line)] hover:bg-[var(--app-surface)]"
                  }`}
                >
                  <option value="">All lists</option>
                  {availableLists.map((l) => (
                    <option key={l.name} value={l.name}>
                      {l.name} ({l.total})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid="boards-filter-clear"
                className="text-[11px] px-2.5 py-1 rounded-full text-muted-foreground hover:text-red-600 shrink-0 flex items-center gap-1"
              >
                <X size={11} /> Clear ({activeFilterCount})
              </button>
            )}
          </div>
          {columns.length === 0 ? (
            <div className="p-10 text-sm text-muted-foreground">
              This board has no columns yet.
            </div>
          ) : (
            <div className="flex gap-3 p-4 h-full min-w-max">
              {columns.map((col) => {
                const allItems = buckets[col.key] || [];
                const items = activeFilterCount > 0 ? allItems.filter(matchesFilters) : allItems;
                const filteredOut = allItems.length - items.length;
                const style = COLOR_STYLES[col.color] || COLOR_STYLES.neutral;
                return (
                  <div
                    key={col.key}
                    className="w-72 shrink-0 flex flex-col rounded-lg bg-white border border-[var(--app-line)] shadow-sm"
                    data-testid={`board-col-${col.key}`}
                    onDragOver={onDragOver}
                    onDrop={onDrop(col.key)}
                  >
                    <div className={`px-3 py-2 border-b ${style.head} rounded-t-lg flex items-center justify-between`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
                        <span className={`text-xs font-semibold truncate ${style.text}`}>
                          {col.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-muted-foreground bg-white/70 rounded-full px-1.5 py-0.5"
                              title={filteredOut > 0 ? `${filteredOut} hidden by filters` : undefined}>
                          {items.length}{filteredOut > 0 ? `/${allItems.length}` : ""}
                        </span>
                        {canClear && items.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setClearColState({
                                key: col.key,
                                label: col.label,
                                count: items.length,
                              });
                            }}
                            title={`Clear all leads in "${col.label}"`}
                            className="p-1 rounded hover:bg-white/70 text-neutral-600 hover:text-red-600 transition-colors"
                            data-testid={`col-clear-${col.key}`}
                          >
                            <Eraser size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px]"
                         data-testid={`board-drop-${col.key}`}>
                      {items.length === 0 && (
                        <div className="text-[11px] text-muted-foreground/60 text-center py-6 border-2 border-dashed border-[var(--app-line)] rounded">
                          Drop a card here
                        </div>
                      )}
                      {items.map((lead) => {
                        const hint = leadNbaHint(lead);
                        return (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={onDragStart(lead.id, col.key)}
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          className="rounded-md border border-[var(--app-line)] bg-white p-2.5 shadow-sm cursor-pointer hover:border-[var(--app-black)] transition-colors active:cursor-grabbing"
                          data-testid={`board-card-${lead.id}`}
                        >
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            <span className="truncate">{lead.name || "Unknown"}</span>
                            {lead.dnc && (
                              <span
                                className="text-[9px] uppercase font-mono px-1 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 shrink-0"
                                title={lead.dnc_reason || "Do Not Call"}
                                data-testid={`board-card-dnc-${lead.id}`}
                              >
                                DNC
                              </span>
                            )}
                          </div>
                          {lead.property_address && (
                            <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                              {lead.property_address}
                            </div>
                          )}
                          {hint && (
                            <div
                              className={`mt-1.5 text-[10px] px-2 py-1 rounded-full border font-medium ${HINT_TONE_STYLE[hint.tone] || HINT_TONE_STYLE.action}`}
                              data-testid={`board-card-hint-${lead.id}`}
                            >
                              {hint.tone === "hot"    ? "🔥 " :
                               hint.tone === "ai"     ? "✨ " :
                               hint.tone === "action" ? "→ " : ""}
                              {hint.text}
                            </div>
                          )}
                          <div className="flex items-center gap-1 mt-1.5 text-[10px]">
                            {lead.motivation_score != null && (
                              <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200">
                                🔥 {lead.motivation_score}
                              </span>
                            )}
                            {lead.property_arv != null && (
                              <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">
                                ARV {money(lead.property_arv)}
                              </span>
                            )}
                            {lead.property_equity != null && (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    title={lead.property_equity_pct != null ? `${lead.property_equity_pct}% equity` : "Est Equity"}>
                                {money(lead.property_equity)}
                                {lead.property_equity_pct != null && (
                                  <span className="ml-0.5 opacity-70">({lead.property_equity_pct}%)</span>
                                )}
                              </span>
                            )}
                            {lead.owner_occupied === false && (
                              <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200"
                                    title="Absentee owner">
                                Absentee
                              </span>
                            )}
                            {lead.listed_for_sale === true && (
                              <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                                    title="On MLS">
                                MLS
                              </span>
                            )}
                            {lead.offer_amount != null && (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {money(lead.offer_amount)}
                              </span>
                            )}
                            {(() => {
                              const payoff = lead.payoff_amount ?? lead.mortgage_balance;
                              if (lead.offer_amount == null || payoff == null) return null;
                              const co = Number(lead.offer_amount) - Number(payoff);
                              return (
                                <span
                                  className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200"
                                  title={`Offer ${money(lead.offer_amount)} − payoff ${money(payoff)}`}
                                  data-testid={`board-card-cashout-${lead.id}`}
                                >
                                  💵 {money(co)}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* New board dialog */}
      <NewBoardDialog open={newBoardOpen} onOpenChange={setNewBoardOpen}
                      onCreate={createBoard} />

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename board</DialogTitle>
          </DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                 data-testid="rename-input" />
          <DialogFooter>
            <Button onClick={renameBoard} data-testid="rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit columns dialog */}
      <EditColumnsDialog open={editColsOpen} onOpenChange={setEditColsOpen}
                         columns={columns} onSave={saveColumns} />

      {/* Bulk CSV → CRM import */}
      <ImportLeadsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        boards={boards}
        defaultBoardId={activeBoardId}
        onImported={(res) => {
          // If the import landed on the currently-visible board, refresh it
          if (res?.board_id === activeBoardId) loadBoardData(activeBoardId);
        }}
      />

      {/* Lead detail drawer */}
      <LeadDetailDrawer
        leadId={drawerLeadId}
        open={!!drawerLeadId}
        onOpenChange={(o) => { if (!o) setDrawerLeadId(null); }}
        onUpdated={() => loadBoardData(activeBoardId)}
      />

      {/* Save-as-template dialog */}
      <SaveTemplateDialog open={saveTplOpen} onOpenChange={setSaveTplOpen}
                          defaultName={activeBoard?.name || ""}
                          onSave={saveAsTemplate} />

      {/* Templates browser */}
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen}
                       onApply={createFromTemplate} />

      {/* Public capture links (admin shares URL with cold callers) */}
      <PublicCaptureLinks
        open={captureLinksOpen}
        onOpenChange={setCaptureLinksOpen}
        boardId={activeBoardId}
        columns={columns}
      />

      <FindOwnerDialog
        open={findOwnerOpen}
        onOpenChange={setFindOwnerOpen}
        boards={boards}
        onPlaced={async () => {
          // Refresh current board so the newly-placed lead shows up right away
          try { await loadBoardData(activeBoardId); } catch { /* no-op */ }
        }}
      />

      {/* Extra-confirmation dialog: clear a single column */}
      <ClearColumnDialog
        state={clearColState}
        onCancel={() => setClearColState(null)}
        onConfirm={confirmClearColumn}
      />

      {/* Bulk-enrich (skip-trace missing phones) dialog */}
      <Dialog open={enrichOpen} onOpenChange={(o) => {
        setEnrichOpen(o);
        if (!o) { setEnrichResult(null); }
      }}>
        <DialogContent className="max-w-lg" data-testid="bulk-enrich-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-emerald-600" />
              Skip-trace leads missing phones
            </DialogTitle>
            <DialogDescription>
              Runs a skip-trace on every CRM lead that has an address but no
              phone number. Cache hits are free — new addresses cost about
              <b> 1 REAPI credit each</b>.
            </DialogDescription>
          </DialogHeader>

          {!enrichResult && enrichPreview && (
            <div className="space-y-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                <div className="font-semibold">
                  {enrichPreview.total_missing} lead
                  {enrichPreview.total_missing === 1 ? "" : "s"} without a phone
                </div>
                {enrichPreview.sample?.length > 0 && (
                  <div className="mt-1.5 text-[11px] opacity-80">
                    e.g. {enrichPreview.sample.slice(0, 3).map((s) => s.address)
                             .join(" · ")}
                    {enrichPreview.total_missing > 3 && " …"}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-xs shrink-0">Batch size</Label>
                <Input
                  data-testid="bulk-enrich-limit"
                  type="number"
                  min={1}
                  max={200}
                  value={enrichLimit}
                  onChange={(e) => setEnrichLimit(e.target.value)}
                  className="w-24 h-9"
                />
                <span className="text-[11px] text-muted-foreground">
                  max 200 per run — spend cap ~{enrichLimit} REAPI credits
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                💡 Same address across multiple leads is only skip-traced once,
                and existing cache entries are hit for free. You can re-run this
                to process the next batch.
              </div>
            </div>
          )}

          {enrichResult && (
            <div className="space-y-3" data-testid="bulk-enrich-result">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800">
                    Found phones
                  </div>
                  <div className="text-2xl font-display font-bold text-emerald-900">
                    {enrichResult.found}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-700">
                    No match
                  </div>
                  <div className="text-2xl font-display font-bold text-slate-900">
                    {enrichResult.not_found}
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground grid grid-cols-2 gap-2">
                <div>Processed: <b>{enrichResult.processed}</b></div>
                <div>REAPI credits used: <b>{enrichResult.reapi_credits_used}</b></div>
                <div className="col-span-2">
                  Remaining without phone: <b>{enrichResult.remaining}</b>
                  {enrichResult.remaining > 0 && (
                    <span className="ml-1 text-emerald-700">
                      · re-run to continue
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {!enrichResult ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEnrichOpen(false)}
                  data-testid="bulk-enrich-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={runBulkEnrich}
                  disabled={enrichRunning || !enrichPreview?.total_missing}
                  data-testid="bulk-enrich-start"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {enrichRunning ? (
                    <><Loader2 size={13} className="animate-spin mr-1" /> Running…</>
                  ) : (
                    <>Start · {Math.min(Number(enrichLimit) || 50, enrichPreview?.total_missing || 0)} leads</>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEnrichOpen(false)}
                  data-testid="bulk-enrich-close"
                >
                  Close
                </Button>
                {enrichResult.remaining > 0 && (
                  <Button
                    onClick={runBulkEnrich}
                    disabled={enrichRunning}
                    data-testid="bulk-enrich-run-more"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {enrichRunning ? (
                      <><Loader2 size={13} className="animate-spin mr-1" /> Running…</>
                    ) : (
                      <>Run next {Math.min(Number(enrichLimit) || 50, enrichResult.remaining)}</>
                    )}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function ClearColumnDialog({ state, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTyped(""); }, [state?.key]);
  if (!state) return null;
  const match = typed.trim().toUpperCase() === "CLEAR";
  const submit = async () => {
    if (!match) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="clear-column-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-red-700">
            <X size={16} /> Clear column
          </DialogTitle>
          <DialogDescription>
            This will permanently delete <strong>{state.count}</strong> lead
            {state.count === 1 ? "" : "s"} from the &ldquo;{state.label}&rdquo; column.
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-xs">
            Type <span className="font-mono bg-red-50 text-red-700 px-1 rounded">CLEAR</span> to confirm
          </Label>
          <Input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="CLEAR"
            className="field-sharp"
            data-testid="clear-column-input"
            onKeyDown={(e) => { if (e.key === "Enter" && match && !busy) submit(); }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!match || busy}
            className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="clear-column-confirm"
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={13} /> : <Eraser size={13} className="mr-2" />}
            Delete {state.count} lead{state.count === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewBoardDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState("");
  const submit = () => {
    if (!name.trim()) return;
    onCreate(name.trim());
    setName("");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="new-board-dialog">
        <DialogHeader>
          <DialogTitle>New pipeline board</DialogTitle>
          <DialogDescription className="text-xs">
            A fresh Kanban board with 3 starter columns.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Board name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g., Cold Calling Q1"
                 className="mt-1" data-testid="new-board-name" />
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="new-board-create">
            <Plus size={13} className="mr-1" /> Create board
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditColumnsDialog({ open, onOpenChange, columns, onSave }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) setRows(columns.map((c) => ({ ...c })));
  }, [open, columns]);

  const update = (i, patch) => setRows((prev) =>
    prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setRows((prev) => [
    ...prev,
    { key: null, label: "New column", color: "neutral", order: prev.length },
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="edit-cols-dialog">
        <DialogHeader>
          <DialogTitle>Edit columns</DialogTitle>
          <DialogDescription className="text-xs">
            Add, rename, recolor or remove columns. Removing a column
            detaches its leads from this board.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2"
                 data-testid={`edit-col-row-${i}`}>
              <select
                value={r.color}
                onChange={(e) => update(i, { color: e.target.value })}
                className="h-9 rounded-md border border-[var(--app-line)] px-2 text-xs"
              >
                {COLOR_KEYS.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <Input
                value={r.label}
                onChange={(e) => update(i, { label: e.target.value })}
                className="flex-1 text-sm"
                data-testid={`edit-col-label-${i}`}
              />
              <button
                onClick={() => remove(i)}
                className="p-2 rounded hover:bg-red-50 text-red-500"
                data-testid={`edit-col-del-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={add}
                  className="rounded-full text-xs h-8"
                  data-testid="edit-col-add">
            <Plus size={12} className="mr-1" /> Add column
          </Button>
        </div>
        <DialogFooter>
          <Button
            onClick={() => onSave(rows.map((r, i) => ({ ...r, order: i })))}
            disabled={rows.length === 0}
            data-testid="edit-col-save"
          >
            <ArrowRight size={13} className="mr-1" /> Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SaveTemplateDialog({ open, onOpenChange, defaultName, onSave }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(defaultName ? `${defaultName} Template` : ""); }, [open, defaultName]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="save-tpl-dialog">
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription className="text-xs">
            Reuse this column layout on future boards.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Template name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1" data-testid="save-tpl-name" />
        </div>
        <DialogFooter>
          <Button onClick={() => { if (name.trim()) onSave(name.trim()); }}
                  data-testid="save-tpl-btn">
            <BookmarkPlus size={13} className="mr-1" /> Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesDialog({ open, onOpenChange, onApply }) {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyId, setApplyId] = useState(null);
  const [applyName, setApplyName] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/board-templates")
      .then((r) => setTpls(r.data.templates || []))
      .finally(() => setLoading(false));
  }, [open]);

  const del = async (tid) => {
    if (!confirm("Delete this template?")) return;
    try {
      await api.delete(`/board-templates/${tid}`);
      setTpls((prev) => prev.filter((t) => t.id !== tid));
      toast.success("Template deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="tpls-dialog">
        <DialogHeader>
          <DialogTitle>My templates</DialogTitle>
          <DialogDescription className="text-xs">
            Click a template to spin up a new board with those exact columns.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : tpls.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed border-[var(--app-line)] rounded p-4 text-center">
              No templates yet. Save a board layout as a template first.
            </div>
          ) : (
            tpls.map((t) => (
              <div key={t.id}
                   className="flex items-center justify-between border border-[var(--app-line)] rounded p-2.5"
                   data-testid={`tpl-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {(t.columns || []).length} columns · {(t.columns || []).map((c) => c.label).slice(0, 3).join(" · ")}
                    {(t.columns || []).length > 3 && ` +${t.columns.length - 3}`}
                  </div>
                </div>
                <Button size="sm" variant="secondary"
                        className="rounded-full text-[11px] h-7"
                        onClick={() => { setApplyId(t.id); setApplyName(t.name); }}
                        data-testid={`tpl-use-${t.id}`}>
                  Use
                </Button>
                <button onClick={() => del(t.id)}
                        className="p-2 rounded hover:bg-red-50 text-red-500 ml-1"
                        data-testid={`tpl-del-${t.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))
          )}
        </div>
        {applyId && (
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">New board name</Label>
            <Input value={applyName} onChange={(e) => setApplyName(e.target.value)}
                   className="mt-1" data-testid="tpl-new-name" />
            <Button onClick={() => onApply(applyId, applyName.trim())}
                    disabled={!applyName.trim()}
                    className="w-full rounded-full"
                    data-testid="tpl-apply-btn">
              <Plus size={13} className="mr-1" /> Create board
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



/* ============================================================================
 * FindOwnerDialog — global CRM lead-finder
 *
 * Type a name / phone / address → 300ms-debounced search hits
 * /api/leads/search, results show current board+column. Pick one → choose
 * target board+column → Place → POST /api/boards/{bid}/move-lead. Solves the
 * "cold caller talked to someone but I can't find them" problem.
 * ============================================================================ */
function FindOwnerDialog({ open, onOpenChange, boards, onPlaced }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [targetBoardId, setTargetBoardId] = useState("");
  const [targetColKey, setTargetColKey] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/leads/search", {
          params: { q: q.trim(), limit: 20 },
        });
        setResults(data?.results || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (!open) {
      setQ(""); setResults([]); setSelected(null);
      setTargetBoardId(""); setTargetColKey("");
    }
  }, [open]);

  useEffect(() => {
    if (!selected) return;
    if (!targetBoardId) {
      const lm = (boards || []).find(b => b.name === "Lead Management") || (boards || [])[0];
      if (lm) {
        setTargetBoardId(lm.id);
        const cols = lm.columns || [];
        if (cols.length) setTargetColKey(cols[0].key);
      }
    }
  }, [selected, boards, targetBoardId]);

  const targetBoard = (boards || []).find(b => b.id === targetBoardId) || null;
  const targetCols  = targetBoard?.columns || [];

  const doPlace = async () => {
    if (!selected || !targetBoardId || !targetColKey) return;
    setPlacing(true);
    try {
      await api.post(`/boards/${targetBoardId}/move-lead`, {
        lead_id:    selected.id,
        column_key: targetColKey,
      });
      const colLabel = targetCols.find(c => c.key === targetColKey)?.label || targetColKey;
      toast.success(`Placed ${selected.name || "lead"} → ${targetBoard.name} · ${colLabel}`);
      onPlaced?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Placement failed");
    } finally { setPlacing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="find-owner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search size={16} className="text-violet-600" />
            Find owner &amp; place into CRM
          </DialogTitle>
          <DialogDescription className="text-xs">
            Search any lead already in the system by owner name, phone, email, or property address.
            Pick a match, choose the target board &amp; column, and drop them in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => { setQ(e.target.value); setSelected(null); }}
              placeholder="Search name · phone · address · email…"
              className="pl-8 h-10"
              data-testid="find-owner-input"
            />
            {searching && (
              <Loader2 size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>

          {!selected && q.trim().length < 2 && (
            <div className="text-[11px] text-muted-foreground italic pl-1">
              Type at least 2 characters to start searching.
            </div>
          )}
          {!selected && q.trim().length >= 2 && results.length === 0 && !searching && (
            <div className="text-[11px] text-muted-foreground italic pl-1">
              No matches. Try a different name, phone, or address.
            </div>
          )}

          {!selected && results.length > 0 && (
            <div className="border border-[var(--app-line)] rounded-lg divide-y divide-[var(--app-line)] max-h-72 overflow-y-auto"
                 data-testid="find-owner-results">
              {results.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="w-full text-left p-3 hover:bg-[var(--app-surface)] transition-colors focus:outline-none focus:bg-violet-50"
                  data-testid={`find-owner-result-${r.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{r.name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.property_address || "—"}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.phone || "no phone"}
                        {r.email ? ` · ${r.email}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {r.board_name ? (
                        <>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            currently in
                          </div>
                          <div className="text-[11px] font-medium">
                            {r.board_name}
                            {r.column_label ? ` · ${r.column_label}` : ""}
                          </div>
                        </>
                      ) : (
                        <div className="text-[10px] italic text-amber-700">
                          Not on any board yet
                        </div>
                      )}
                      {r.last_disposition && (
                        <div className="text-[10px] mt-0.5">
                          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {r.last_disposition}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-3 space-y-3"
                 data-testid="find-owner-selection">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-violet-700">Selected</div>
                  <div className="font-semibold text-sm">{selected.name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {selected.property_address || "—"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {selected.phone || "no phone"}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="find-owner-change-selection"
                >
                  Change
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Target board</Label>
                  <select
                    value={targetBoardId}
                    onChange={(e) => {
                      setTargetBoardId(e.target.value);
                      const b = (boards || []).find(x => x.id === e.target.value);
                      const cols = b?.columns || [];
                      setTargetColKey(cols[0]?.key || "");
                    }}
                    className="mt-1 h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
                    data-testid="find-owner-board-select"
                  >
                    <option value="">— Pick a board —</option>
                    {(boards || []).map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px]">Target column</Label>
                  <select
                    value={targetColKey}
                    onChange={(e) => setTargetColKey(e.target.value)}
                    disabled={!targetBoardId}
                    className="mt-1 h-9 w-full rounded-md border border-input px-2 text-sm bg-white disabled:opacity-50"
                    data-testid="find-owner-col-select"
                  >
                    <option value="">— Pick a column —</option>
                    {targetCols.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="find-owner-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={doPlace}
            disabled={!selected || !targetBoardId || !targetColKey || placing}
            data-testid="find-owner-place-btn"
            className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
          >
            {placing
              ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Placing…</>
              : <>Place lead <ArrowRight size={12} className="ml-1.5" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
